/**
 * Zusatzfunktionen je Basar (Plan Welle 3, F0). Vier Schalter, Standard aus. Ausgeschaltet sieht niemand etwas davon
 * (keine Kachel, kein Menüpunkt, keine Mail, keine Mitteilung), und jede Route der Funktion antwortet mit 404.
 *
 * Quelle der Wahrheit für Server und Web: Schlüssel, Feld der API, Spalte in `events`, Titel und Beschreibung.
 *
 * Freigabe: Jedes Paket setzt mit SEINEM Merge nur die eigene Zeile `released` auf `true`. Solange eine Funktion nicht
 * freigegeben ist, erscheint ihr Schalter nicht in der Oberfläche, und `PATCH /events/:id` lehnt `true` mit 400 ab.
 * Eine Vorlage mit eingeschaltetem, aber nicht freigegebenem Schalter wird beim Anwenden nicht abgelehnt, der Schalter
 * steht dann auf aus (`effectiveFeatureFlags`).
 */

export const EVENT_FEATURE_KEYS = ['runSheet', 'push', 'payoutTransfer', 'helperBonus'] as const;
export type EventFeatureKey = (typeof EVENT_FEATURE_KEYS)[number];

export function isEventFeatureKey(value: string): value is EventFeatureKey {
  return (EVENT_FEATURE_KEYS as readonly string[]).includes(value);
}

export interface EventFeatureDef {
  key: EventFeatureKey;
  /** Feld in der API (`PATCH /events/:id`, Antwort) und in `EVENT_SETTING_FIELDS`. */
  field: `${EventFeatureKey}Enabled`;
  /** Spalte in `events` (Migration 0068). */
  column: string;
  title: string;
  /** Ein Satz aus Kundensicht: was der Schalter bewirkt. */
  description: string;
  /** Erst mit dem Merge des zugehörigen Pakets `true`. */
  released: boolean;
  /** Pfad der Hilfeseite zur Funktion (vorgemerkt: das Paket trägt ihn mit seiner Freigabe ein), die Einstellungen zeigen den Link. */
  helpPath?: string;
}

export const EVENT_FEATURES: readonly EventFeatureDef[] = [
  {
    key: 'runSheet',
    field: 'runSheetEnabled',
    column: 'run_sheet_enabled',
    title: 'Laufzettel an der Kasse',
    description:
      'Helfer erfassen die Etiketten im Korb schon in der Schlange. Die Kasse übernimmt den Korb mit einem Scan.',
    released: true,
    helpPath: '/hilfe/laufzettel',
  },
  {
    key: 'push',
    field: 'pushEnabled',
    column: 'push_enabled',
    title: 'Mitteilungen an Verkäufer',
    description:
      'Verkäufer bekommen kurze Mitteilungen auf ihr Gerät, zum Beispiel wenn Artikel verkauft wurden.',
    // W3-Push-a (Web-Push im Browser und in der installierten Web-App); native Mitteilungen folgen mit Push-b
    released: true,
    helpPath: '/funktionen#mitteilungen',
  },
  {
    key: 'payoutTransfer',
    field: 'payoutTransferEnabled',
    column: 'payout_transfer_enabled',
    title: 'Auszahlung per Überweisung',
    description:
      'Verkäufer können statt Bargeld die Überweisung wählen. Die Verwaltung bekommt GiroCode und Datei für das Online-Banking. BARSO überweist nicht selbst. Wer „Überweisung“ gewählt hat, wird beim Ausschalten wieder auf bar gestellt (mit Mail).',
    released: true,
    helpPath: '/funktionen#abrechnung',
  },
  {
    key: 'helperBonus',
    field: 'helperBonusEnabled',
    column: 'helper_bonus_enabled',
    title: 'Helferbonus und Einlass',
    description:
      'Helfer mit Schichten bekommen mehr Artikel, weniger Provision oder früheren Einlass (mit Einlass-Pass und Station „Einlass“), die Verwaltung kann zum Beispiel „Orga-Team“ zuweisen. Der Schalter lässt sich ab der Annahme nicht mehr ändern.',
    // W3-Bonus-a (Stufen, Artikel, Provision, Kennzeichen), W3-Bonus-b (Einlass-Pass, Station „Einlass“)
    released: true,
    helpPath: '/funktionen#helferbonus',
  },
];

const BY_KEY = new Map<EventFeatureKey, EventFeatureDef>(
  EVENT_FEATURES.map((feature) => [feature.key, feature]),
);

export function eventFeature(key: EventFeatureKey): EventFeatureDef {
  const def = BY_KEY.get(key);
  if (def === undefined) throw new Error(`Unbekannte Zusatzfunktion: ${key}`);
  return def;
}

/** Ist die Funktion schon für Kunden freigegeben (Paket gemergt)? */
export function isFeatureReleased(key: EventFeatureKey): boolean {
  return eventFeature(key).released;
}

/** Die API-Felder der Schalter, die schon freigegeben sind (nur diese darf `PATCH` einschalten). */
export function releasedFeatureFields(): string[] {
  return EVENT_FEATURES.filter((feature) => feature.released).map((feature) => feature.field);
}

/** Schalterstände je Funktion (Blaupause, Antwort der API). */
export type EventFeatureFlags = Record<EventFeatureKey, boolean>;

export const NO_FEATURES: Readonly<EventFeatureFlags> = {
  runSheet: false,
  push: false,
  payoutTransfer: false,
  helperBonus: false,
};

/**
 * Die Schalter, die beim Anwenden einer Vorlage oder beim Wiederholen wirklich gelten: Fehlt `features` (alte Vorlage),
 * ist alles aus; ein nicht freigegebener Schalter wird auf aus gesetzt, ohne Fehler.
 * `released` lässt sich für Tests überschreiben.
 */
export function effectiveFeatureFlags(
  requested: Partial<EventFeatureFlags> | undefined,
  isReleased: (key: EventFeatureKey) => boolean = isFeatureReleased,
): EventFeatureFlags {
  const result: EventFeatureFlags = { ...NO_FEATURES };
  if (requested === undefined) return result;
  for (const key of EVENT_FEATURE_KEYS) {
    result[key] = requested[key] === true && isReleased(key);
  }
  return result;
}
